import { useMemo } from "react";
import { TrendingUp, Users, Wrench, Receipt, DollarSign } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import Card from "../ui/Card";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import { formatUSD } from "../../lib/formatters";
import { monthKeyFromDate, today, formatDate } from "../../lib/dateUtils";
import {
  aggregateOfferMonth, aggregateOfferAllTime, getOfferPnlSeries,
} from "../../lib/calculations";

export default function OfferDashboard({ offer, state, onOpenClient }) {
  const mKey = monthKeyFromDate(today());

  const thisMonth = useMemo(
    () => aggregateOfferMonth(offer.id, state, mKey),
    [offer.id, state, mKey]
  );
  const allTime = useMemo(
    () => aggregateOfferAllTime(offer.id, state),
    [offer.id, state]
  );
  const series = useMemo(
    () => getOfferPnlSeries(offer.id, state, 6),
    [offer.id, state]
  );

  const clientsOnOffer = (state.clients || []).filter(
    (c) => c.primaryOfferId === offer.id
  );
  const activeClientCount = clientsOnOffer.filter((c) => c.status === "active").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">{offer.name}</h3>
        <Pill status={offer.type === "recurring" ? "recurring" : "oneoff"}>{offer.type}</Pill>
        {offer.department && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300">
            {offer.department}
          </span>
        )}
        <span className="text-xs text-brand-slate">
          {clientsOnOffer.length} client{clientsOnOffer.length === 1 ? "" : "s"}
          {" · "}
          {activeClientCount} active
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PnlCard label="This month" data={thisMonth} />
        <PnlCard label="All time" data={allTime} />
      </div>

      <Card title={<span className="inline-flex items-center gap-2"><TrendingUp size={15} /> P&L (last 6 months)</span>}>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => formatUSD(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
              <Bar dataKey="cogs" fill="#f59e0b" name="COGS" />
              <Bar dataKey="opex" fill="#a855f7" name="OPEX" />
              <Bar dataKey="netProfit" fill="#3480f1" name="Net Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ContributorList
          title={<span className="inline-flex items-center gap-2"><Users size={15} /> Team contributing (this month)</span>}
          cogs={thisMonth.contributingTeam.cogs}
          opex={thisMonth.contributingTeam.opex}
          emptyMsg="No team members allocated to this offer."
          rowLabel="role"
        />
        <ContributorList
          title={<span className="inline-flex items-center gap-2"><Wrench size={15} /> Tools contributing (this month)</span>}
          cogs={thisMonth.contributingTools.cogs}
          opex={thisMonth.contributingTools.opex}
          emptyMsg="No tools allocated to this offer."
        />
      </div>

      {(thisMonth.oneOffsByType.cogs.length > 0 || thisMonth.oneOffsByType.opex.length > 0) && (
        <Card title={<span className="inline-flex items-center gap-2"><Receipt size={15} /> One-off expenses this month</span>}>
          <Table>
            <THead>
              <TR><TH>Name</TH><TH>Class</TH><TH>Category</TH><TH>Date</TH><TH className="text-right">Amount</TH></TR>
            </THead>
            <tbody>
              {[...thisMonth.oneOffsByType.cogs, ...thisMonth.oneOffsByType.opex].map((o) => {
                const isCogs = (o.costType || "opex") === "cogs";
                return (
                  <TR key={o.id}>
                    <TD className="font-medium">{o.name}</TD>
                    <TD>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${
                        isCogs
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {isCogs ? "COGS" : "OPEX"}
                      </span>
                    </TD>
                    <TD className="text-brand-slate text-xs">{o.category || "—"}</TD>
                    <TD>{formatDate(o.date)}</TD>
                    <TD className="text-right">{formatUSD(o.amount)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <Card title={<span className="inline-flex items-center gap-2"><DollarSign size={15} /> Clients on this offer</span>}>
        {clientsOnOffer.length === 0 ? (
          <EmptyState title="No clients on this offer yet" message="Assign clients to this offer from the Clients tab." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Client</TH>
                <TH>Status</TH>
                <TH className="text-right">Monthly rate</TH>
                <TH>Started</TH>
              </TR>
            </THead>
            <tbody>
              {clientsOnOffer.map((c) => (
                <TR key={c.id}>
                  <TD className="font-medium">
                    {onOpenClient ? (
                      <button
                        type="button"
                        onClick={() => onOpenClient(c.id)}
                        className="text-left hover:text-brand-blue-dark hover:underline cursor-pointer"
                      >
                        {c.name}
                      </button>
                    ) : (
                      <span>{c.name}</span>
                    )}
                  </TD>
                  <TD><Pill status={c.status}>{c.status}</Pill></TD>
                  <TD className="text-right">{c.monthlyRate ? formatUSD(c.monthlyRate) : "—"}</TD>
                  <TD>{formatDate(c.startDate)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// Renders the 5-line P&L stack (Revenue → COGS → Gross Profit → OPEX →
// Net Profit) plus the cost composition breakdown underneath. Used for
// both per-offer and agency-wide views.
export function PnlCard({ label, data }) {
  const { revenue, cogs, opex, grossProfit, netProfit } = data;
  const gpPct = revenue > 0 ? (grossProfit / revenue) * 100 : null;
  const npPct = revenue > 0 ? (netProfit / revenue) * 100 : null;
  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-4">
      <div className="text-[11px] uppercase tracking-wide text-brand-slate mb-2">{label}</div>
      <PnlRow label="Revenue" value={revenue} tone="green" />
      <PnlRow label="COGS" value={-cogs.total} tone="amber" hint={breakdownHint(cogs)} />
      <div className="border-t border-dashed border-gray-200 dark:border-[var(--border-soft)] my-1.5" />
      <PnlRow
        label="Gross Profit"
        value={grossProfit}
        tone={grossProfit >= 0 ? "blue" : "red"}
        bold
        pctLabel={gpPct != null ? `${gpPct.toFixed(0)}%` : ""}
      />
      <PnlRow label="OPEX" value={-opex.total} tone="amber" hint={breakdownHint(opex)} />
      <div className="border-t-2 border-gray-300 dark:border-[var(--border-soft)] my-1.5" />
      <PnlRow
        label="Net Profit"
        value={netProfit}
        tone={netProfit >= 0 ? "blue" : "red"}
        bold
        pctLabel={npPct != null ? `${npPct.toFixed(0)}%` : ""}
      />
    </div>
  );
}

function PnlRow({ label, value, tone, hint, bold, pctLabel }) {
  const toneClass = {
    green: "text-emerald-700 dark:text-emerald-300",
    amber: "text-amber-700 dark:text-amber-400",
    blue: "text-brand-blue-dark dark:text-brand-blue",
    red: "text-red-600 dark:text-red-400",
  }[tone] || "";
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-baseline gap-1.5">
        <span className={`text-sm ${bold ? "font-display font-semibold" : "text-brand-slate"}`}>{label}</span>
        {hint && <span className="text-[10px] text-brand-slate">{hint}</span>}
      </div>
      <div className="flex items-baseline gap-2">
        {pctLabel && <span className="text-[11px] text-brand-slate font-medium">{pctLabel}</span>}
        <span className={`${bold ? "text-base font-display font-semibold" : "text-sm font-medium"} ${toneClass}`}>
          {formatUSD(value)}
        </span>
      </div>
    </div>
  );
}

function breakdownHint(part) {
  const parts = [];
  if (part.team > 0) parts.push(`team ${formatUSD(part.team)}`);
  if (part.tools > 0) parts.push(`tools ${formatUSD(part.tools)}`);
  if (part.oneOffs > 0) parts.push(`one-off ${formatUSD(part.oneOffs)}`);
  return parts.join(" · ");
}

function ContributorList({ title, cogs, opex, emptyMsg, rowLabel }) {
  const hasAny = cogs.length > 0 || opex.length > 0;
  return (
    <Card title={title}>
      {!hasAny ? (
        <p className="text-xs text-brand-slate italic">{emptyMsg}</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              {rowLabel === "role" && <TH>Role</TH>}
              <TH>Class</TH>
              <TH className="text-right">Cost</TH>
            </TR>
          </THead>
          <tbody>
            {cogs.map((r) => (
              <TR key={`cogs-${r.id}`}>
                <TD className="font-medium">{r.name}</TD>
                {rowLabel === "role" && <TD className="text-brand-slate text-xs">{r.role}</TD>}
                <TD>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200">
                    COGS
                  </span>
                </TD>
                <TD className="text-right">{formatUSD(r.cost)}</TD>
              </TR>
            ))}
            {opex.map((r) => (
              <TR key={`opex-${r.id}`}>
                <TD className="font-medium">{r.name}</TD>
                {rowLabel === "role" && <TD className="text-brand-slate text-xs">{r.role}</TD>}
                <TD>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border bg-amber-50 text-amber-700 border-amber-200">
                    OPEX
                  </span>
                </TD>
                <TD className="text-right">{formatUSD(r.cost)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}